// apps/web/src/components/layout/SortableItem.tsx
//
// Phase 9 Plan 11 Task 1 — SortableItem wrapper.
//
// Shape per Plan 09-11 must_haves §"truths" + RESEARCH lines 2099-2130:
//   - useSortable({ id }) returns attributes, listeners, setNodeRef, transform, transition.
//   - Outer div with ref={setNodeRef} and style={{ transform: CSS.Transform.toString(transform), transition }}
//     (documented inline-style carve-out — Plan 09-13's grep gate extends the allowlist).
//   - drag-handle <button> with aria-label={dragHandleAriaLabel} aria-roledescription="sortable"
//     + {...attributes} {...listeners}.
//   - lucide GripVertical icon inside the button.
//
// INLINE STYLE CARVE-OUT — Phase 9 documented (UI-SPEC §"Sanitization &
// Security Contract" + Plan 09-13 grep-gate allowlist extension):
//   style={{ transform: CSS.Transform.toString(transform), transition }}
// is required by @dnd-kit/sortable because the library computes the drag
// translate as a CSS transform string at runtime. The grep gate's allowlist
// covers this file + the `transform` + `transition` properties only. No user-
// string flows into the style — `transform` and `transition` are produced
// internally by @dnd-kit from numeric drag-state values.
//
// ACCESSIBILITY: aria-roledescription="sortable" on the drag handle is the
// Mission Critical accessibility contract (T-09-11-03 mitigation). The
// aria-label carries the live useTerm()-rendered string from the caller.
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type * as React from "react";

export interface SortableItemProps {
  id: string;
  dragHandleAriaLabel: string;
  children: React.ReactNode;
}

export function SortableItem(props: SortableItemProps): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.id,
  });

  // DOCUMENTED INLINE STYLE CARVE-OUT — @dnd-kit/utilities CSS.Transform.toString
  // produces the runtime drag translate from numeric state. This is one of the
  // two Phase 9 inline-style carve-outs (the other lives in PredicateNode.tsx
  // for the per-depth marginLeft indent). Plan 09-13's grep gate covers this.
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <button
        type="button"
        {...attributes}
        {...listeners}
        // Override @dnd-kit's default attributes so the aria-label always
        // reflects the live useTerm() string the caller supplied, and so the
        // aria-roledescription remains the documented "sortable" value (which
        // @dnd-kit also sets, but the spread above could in principle be empty
        // on older versions — pinning here makes the test contract explicit).
        aria-label={props.dragHandleAriaLabel}
        aria-roledescription="sortable"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md p-2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical size={16} aria-hidden />
      </button>
      <div className="flex-1 min-w-0">{props.children}</div>
    </div>
  );
}
SortableItem.displayName = "SortableItem";
